import { useCallback, useMemo, useState } from 'react';

/**
 * Ungespeicherte Änderungen als Überlagerung über den aktuellen Stand (der sich per Ereignis ändern kann).
 * `value` = Stand mit Änderungen, `patch` = nur tatsächlich geänderte Felder.
 */
export function useEdits<T extends object>(base: T | undefined) {
  const [edits, setEdits] = useState<Partial<T>>({});
  const patch = useMemo(() => {
    const out: Partial<T> = {};
    if (!base) return out;
    for (const k of Object.keys(edits) as (keyof T)[]) {
      if (!Object.is(edits[k], base[k]) && JSON.stringify(edits[k]) !== JSON.stringify(base[k])) out[k] = edits[k];
    }
    return out;
  }, [edits, base]);
  const value = useMemo(() => (base ? ({ ...base, ...edits } as T) : undefined), [base, edits]);
  const set = useCallback(<K extends keyof T>(key: K, v: T[K]) => setEdits((e) => ({ ...e, [key]: v })), []);
  const reset = useCallback(() => setEdits({}), []);
  return { value, patch, dirty: Object.keys(patch).length > 0, set, reset };
}
